import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  CREATE_ANCHORED_COMMENT,
  CREATE_COMMENT,
  DELETE_COMMENT_THREAD,
  GET_COMMENT_ANCHORS,
  GET_COMMENTS_FLAT,
  REOPEN_COMMENT_ANCHOR,
  RESOLVE_COMMENT_ANCHOR,
  SOFT_REMOVE_COMMENT,
} from "../../../services/graphql/queriesMutations";
import type { CommentAnchorType } from "../../../services/types";
import type { AnchoredSelectionDraft } from "../../anchored-comments";
import type { CommentType } from "../../comments";
import type { DiscussionWorkspaceAdapter } from "../model/types";

type UseGistDiscussionWorkspaceAdapterArgs = {
  gistId: number | null | undefined;
  editId?: number | null;
  title?: string;
  subtitle?: string;
  enabled?: boolean;
  user: any;
};

function normalizeComment(comment: any): CommentType | null {
  if (!comment || typeof comment.id !== "number") return null;

  const normalizedReplies = Array.isArray(comment.replies)
    ? comment.replies
        .map((reply: any) => normalizeComment(reply))
        .filter((reply: CommentType | null): reply is CommentType => Boolean(reply))
    : [];

  return {
    ...comment,
    comment: typeof comment.comment === "string" ? comment.comment : "",
    parentId: typeof comment.parentId === "number" ? comment.parentId : null,
    userId: typeof comment.userId === "number" ? comment.userId : 0,
    user: comment.user || {
      id: typeof comment.userId === "number" ? comment.userId : 0,
      name: "Anonymous",
    },
    createdAt: comment.createdAt || "",
    updatedAt: comment.updatedAt || comment.createdAt || "",
    replies: normalizedReplies,
  };
}

function normalizeCommentList(comments: any[]): CommentType[] {
  if (!Array.isArray(comments)) return [];
  return comments
    .map((comment) => normalizeComment(comment))
    .filter((comment: CommentType | null): comment is CommentType => Boolean(comment));
}

function sortAnchorsByCreatedAtThenId(
  a: CommentAnchorType,
  b: CommentAnchorType,
): number {
  const aTime = Date.parse(a.createdAt || "");
  const bTime = Date.parse(b.createdAt || "");

  if (Number.isFinite(aTime) && Number.isFinite(bTime) && aTime !== bTime) {
    return aTime - bTime;
  }

  return a.id - b.id;
}

function mergeCommentAnchorList(
  anchors: CommentAnchorType[],
  nextAnchor: CommentAnchorType,
): CommentAnchorType[] {
  const byId = new Map<number, CommentAnchorType>();
  anchors.forEach((anchor) => byId.set(anchor.id, anchor));
  byId.set(nextAnchor.id, nextAnchor);
  return Array.from(byId.values()).sort(sortAnchorsByCreatedAtThenId);
}

export function useGistDiscussionWorkspaceAdapter({
  gistId,
  editId,
  title,
  subtitle,
  enabled = true,
  user,
}: UseGistDiscussionWorkspaceAdapterArgs): DiscussionWorkspaceAdapter {
  const hasGist = typeof gistId === "number" && Number.isFinite(gistId);
  const hasEdit = typeof editId === "number" && Number.isFinite(editId);
  const isReady = Boolean(enabled && hasGist);
  const isAnchorReady = Boolean(isReady && hasEdit);

  const {
    data: commentsData,
    loading: commentsLoading,
    error: commentsError,
    refetch: refetchCommentsQuery,
  } = useQuery(GET_COMMENTS_FLAT, {
    variables: { gistId: gistId ?? 0 },
    skip: !isReady,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: anchorsData,
    loading: anchorsLoading,
    error: anchorsError,
    refetch: refetchAnchorsQuery,
  } = useQuery(GET_COMMENT_ANCHORS, {
    variables: {
      gistId: gistId ?? 0,
      editId: editId ?? null,
      status: "ACTIVE",
    },
    skip: !isAnchorReady,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const [createComment] = useMutation(CREATE_COMMENT);
  const [createAnchoredComment] = useMutation(CREATE_ANCHORED_COMMENT);
  const [softRemoveComment] = useMutation(SOFT_REMOVE_COMMENT);
  const [deleteCommentThread] = useMutation(DELETE_COMMENT_THREAD);
  const [resolveAnchorMutation] = useMutation(RESOLVE_COMMENT_ANCHOR);
  const [reopenAnchorMutation] = useMutation(REOPEN_COMMENT_ANCHOR);
  const [localAnchors, setLocalAnchors] = useState<CommentAnchorType[]>([]);

  useEffect(() => {
    setLocalAnchors([]);
  }, [gistId, editId]);

  const comments = useMemo(
    () => normalizeCommentList(commentsData?.commentsFlat || []),
    [commentsData],
  );

  const remoteAnchors = useMemo<CommentAnchorType[]>(
    () => (Array.isArray(anchorsData?.commentAnchors) ? anchorsData.commentAnchors : []),
    [anchorsData],
  );

  const anchors = useMemo(() => {
    const byId = new Map<number, CommentAnchorType>();
    remoteAnchors.forEach((anchor) => byId.set(anchor.id, anchor));
    localAnchors.forEach((anchor) => byId.set(anchor.id, anchor));
    return Array.from(byId.values()).sort(sortAnchorsByCreatedAtThenId);
  }, [localAnchors, remoteAnchors]);

  const isCommentModerator = user?.userType === "admin" || user?.userType === "moderator";

  const refetch = useCallback(async () => {
    if (!isReady || !hasGist) return;

    try {
      await refetchCommentsQuery({ gistId: gistId ?? 0 });
    } catch (error) {
      console.warn("Canonical Comment Workspace: comments refetch failed.", error);
    }

    if (isAnchorReady) {
      try {
        await refetchAnchorsQuery({
          gistId: gistId ?? 0,
          editId: editId ?? null,
          status: "ACTIVE",
        });
      } catch (error) {
        console.warn("Canonical Comment Workspace: anchors refetch failed.", error);
      }
    }
  }, [editId, gistId, hasGist, isAnchorReady, isReady, refetchAnchorsQuery, refetchCommentsQuery]);

  const createRootComment = useCallback(
    async (content: string) => {
      const trimmed = content.trim();
      if (!trimmed) return;

      if (!user) {
        window.alert("Please login to add a comment.");
        return;
      }

      if (!hasGist) {
        throw new Error("A Gist is required to create a discussion comment.");
      }

      await createComment({
        variables: {
          newComment: {
            comment: trimmed,
            parentId: null,
            gistId,
            editId: editId ?? null,
          },
        },
      });

      await refetch();
    },
    [createComment, editId, gistId, hasGist, refetch, user],
  );

  const replyToComment = useCallback(
    async (parentCommentId: number, content: string) => {
      const trimmed = content.trim();
      if (!trimmed) return;

      if (!user) {
        window.alert("Please login to reply to comments.");
        return;
      }

      if (!hasGist) {
        throw new Error("A Gist is required to reply to a discussion comment.");
      }

      await createComment({
        variables: {
          newComment: {
            comment: trimmed,
            parentId: parentCommentId,
            gistId,
            editId: editId ?? null,
          },
        },
      });

      await refetch();
    },
    [createComment, editId, gistId, hasGist, refetch, user],
  );

  const handleCreateAnchoredComment = useCallback(
    async (draft: AnchoredSelectionDraft, content: string) => {
      const trimmed = content.trim();
      if (!trimmed) return;

      if (!user) {
        window.alert("Please login to create anchored comments.");
        return;
      }

      if (!hasGist || !hasEdit) {
        throw new Error("A Gist and its latest edit are required to create an anchored discussion.");
      }

      const result = await createAnchoredComment({
        variables: {
          input: {
            gistId,
            editId,
            comment: trimmed,
            target: "BODY",
            selectedText: draft.selectedText,
            selectedHash: draft.selectedHash,
            startOffset: draft.startOffset,
            endOffset: draft.endOffset,
            blockIndex: draft.blockIndex,
            prefixText: draft.prefixText,
            suffixText: draft.suffixText,
            contextText: draft.contextText,
          },
        },
      });

      const createdAnchor = result.data?.createAnchoredComment as CommentAnchorType | undefined;
      if (createdAnchor) {
        setLocalAnchors((previousAnchors) => mergeCommentAnchorList(previousAnchors, createdAnchor));
      }

      await refetch();
      return createdAnchor;
    },
    [createAnchoredComment, editId, gistId, hasEdit, hasGist, refetch, user],
  );

  const handleSoftRemoveComment = useCallback(
    async (commentId: number) => {
      if (!user) {
        window.alert("Please login to remove comments.");
        return;
      }

      await softRemoveComment({ variables: { id: commentId } });
      await refetch();
    },
    [refetch, softRemoveComment, user],
  );

  const handleHardDeleteThread = useCallback(
    async (commentId: number) => {
      if (!isCommentModerator) {
        window.alert("Only moderators and administrators can permanently delete comment threads.");
        return;
      }

      await deleteCommentThread({ variables: { id: commentId } });
      await refetch();
    },
    [deleteCommentThread, isCommentModerator, refetch],
  );

  const resolveAnchor = useCallback(
    async (anchorId: number) => {
      await resolveAnchorMutation({ variables: { id: anchorId } });
      await refetch();
    },
    [refetch, resolveAnchorMutation],
  );

  const reopenAnchor = useCallback(
    async (anchorId: number) => {
      await reopenAnchorMutation({ variables: { id: anchorId } });
      await refetch();
    },
    [refetch, reopenAnchorMutation],
  );

  return {
    target: {
      kind: "gist",
      id: hasGist ? String(gistId) : "",
      title,
      subtitle,
    },
    comments,
    anchors,
    loading: commentsLoading || anchorsLoading,
    error: commentsError || anchorsError,
    currentUserId: typeof user?.id === "number" ? user.id : undefined,
    currentUserRole: user?.userType || undefined,
    canComment: Boolean(user && hasGist),
    canCreateAnchoredComment: Boolean(user && hasGist && hasEdit),
    canModerate: isCommentModerator,
    createRootComment,
    replyToComment,
    createAnchoredComment: handleCreateAnchoredComment,
    resolveAnchor,
    reopenAnchor,
    softRemoveComment: handleSoftRemoveComment,
    hardDeleteThread: handleHardDeleteThread,
    refetch,
  };
}
